import { Icon, Input, InputGroup, InputProps } from '@chakra-ui/react';
import { formatInteger } from '@monetize/utils/core';
import React, {
  FunctionComponent as FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MdSearch } from 'react-icons/md';
import { MCloseButton, MText } from '.';
import { useDebounce } from '../../hooks/useDebounce';

interface SearchInputProps extends Omit<InputProps, 'onChange' | 'value'> {
  /**
   * Optional search term value
   * If passed in, the value is assumed to be controlled from the parent
   */
  value?: string;
  /**
   * If true, shows the count of visible and total results
   * Must also pass in `visibleCount` and `totalCount`
   */
  showCount?: boolean;
  /**
   * Number of visible results
   * Only shown if `showCount` is true
   */
  visibleCount?: number;
  /**
   * Total number of results
   * Only shown if `showCount` is true
   */
  totalCount?: number;
  onChange: (value: string) => void;
}

// Matches v2's `InputLeftElement w="8"`; drops v3's default `px: 3` so the
// icon centers in the 32px slot.
const START_ELEMENT_PROPS = { w: '8', px: '0' } as const;

/**
 * Generic search input without a dropdown
 */
export const SearchInput: FC<SearchInputProps> = React.forwardRef<
  any,
  SearchInputProps
>(
  (
    {
      value = '',
      onChange,
      visibleCount = 0,
      totalCount = 0,
      showCount,
      ...inputProps
    }: SearchInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [internalValue, setInternalValue] = useState(value);
    const debouncedValue = useDebounce(internalValue);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    useEffect(() => {
      onChange(debouncedValue);
    }, [debouncedValue]);

    useEffect(() => {
      // Use setTimeout to ensure DOM is fully rendered before focusing
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);

      return () => clearTimeout(timer);
    }, []);

    const handleOnKeyDown = (ev: React.KeyboardEvent) => {
      if (ev.key === 'Enter') {
        onChange(internalValue);
      } else if (ev.key === 'Escape') {
        setInternalValue('');
        onChange('');
      }
    };

    const mainInputP = 4;
    let inputRightElementW = 70;

    if (internalValue) {
      inputRightElementW = 88;
    }

    // `InputGroup` (not a bare relative `Box`) keeps both elements pinned to
    // the input's own box: v3's `InputElement` has no `top`, so outside a
    // flex group the start icon centers against the taller inline line box
    // and the end element drops below the input. It also pads the input
    // start past the icon, as v2's `InputGroup` did.
    return (
      <InputGroup
        startElement={
          <Icon w="4" h="4" color="tPurple.base" asChild>
            <MdSearch />
          </Icon>
        }
        startElementProps={START_ELEMENT_PROPS}
        endElement={
          <>
            {!!internalValue && showCount && (
              <MText fontSize="xs" mr="2" whiteSpace="nowrap">
                {!visibleCount ? (
                  'No Results'
                ) : (
                  <>
                    Showing {formatInteger(visibleCount)} of{' '}
                    {formatInteger(totalCount)}
                  </>
                )}
              </MText>
            )}
            {internalValue && (
              <MCloseButton
                onClick={() => {
                  setInternalValue('');
                  onChange('');
                }}
                mr="2"
              />
            )}
          </>
        }
        endElementProps={{
          w: `${inputRightElementW}px`,
          px: '0',
          justifyContent: 'flex-end',
        }}
      >
        <Input
          data-testid="search-input"
          type="text"
          ref={inputRef}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          onKeyDown={handleOnKeyDown}
          _placeholder={{
            color: 'tGray.darkGrayPurple',
          }}
          // Logical `pe`, not `pr`: it replaces the `pe` InputGroup injects
          // instead of competing with it.
          pe={`${inputRightElementW + mainInputP}px`}
          {...inputProps}
        />
      </InputGroup>
    );
  },
);
